import { Box, Button } from '@mui/material'
import moment from 'moment'
import { useCallback } from 'react'
import { Controller } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { DateTimePickerSplitComponent } from 'src/components/DateTimePickerSplit'
import { getLocalFormattedDateTimeFromUTCString } from 'src/utils/function'
import { z } from 'zod'
import { useGetAllResources } from '../../../api/task-resource'
import { useGetTimeReportTypes } from '../../../api/time-report-type'
import { AppForm, FormSelectField, FormSubmitButton, FormTextField } from '../../../components/FormCore'
import { useZodForm } from '../../../hooks/zod-form'
import { APIResultCount } from 'src/constant'

const TimeReportForm = ({ initialValues, onSubmit, pop, customerId, isUpdateForm = false }) => {
  const { t: translate } = useTranslation(['time-reports', 'common'])
  const { data: resources } = useGetAllResources(customerId, 0, APIResultCount.Max)
  const { data: timeReportTypes } = useGetTimeReportTypes(0, APIResultCount.Max)

  const schema = z
    .object({
      fromDateTime: z.any().refine(data => data != undefined, translate('startDateRequired')),
      toDateTime: z.any().refine(data => data != undefined, translate('endDateRequired')),
      description: z
        .string()
        .min(1, translate('descriptionIsRequired', { ns: 'common' }))
        .max(400),
      timeReportTypeId: z.any().transform(value => (typeof value == 'object' ? value?.id : value)),
      resourceId: z.any().transform(value => (typeof value == 'object' ? value?.id : value)),
    })
    .refine(data => data.timeReportTypeId > 0, {
      message: translate('timeReportTypeRequired'),
      path: ['timeReportTypeId'],
    })
    .refine(data => data.resourceId > 0, {
      message: translate('resourceRequired'),
      path: ['resourceId'],
    })
    .refine(data => !(data?.toDateTime && data?.toDateTime <= data.fromDateTime), {
      message: translate('endStartDateError', { ns: 'common' }),
      path: ['toDateTime'],
    })

  const form = useZodForm(schema, {
    defaultValues: {
      ...initialValues,
      timeReportTypeId: initialValues?.timeReportType,
      resourceId: initialValues?.resource,
      toDateTime: initialValues?.toDateTime
        ? moment(getLocalFormattedDateTimeFromUTCString(initialValues.toDateTime, 'YYYY-MM-DDTHH:mm')).toDate()
        : null,
      fromDateTime: initialValues?.fromDateTime
        ? moment(getLocalFormattedDateTimeFromUTCString(initialValues.fromDateTime, 'YYYY-MM-DDTHH:mm')).toDate()
        : null,
    },
  })

  const onSubmitHandle = useCallback(
    async formValues => {
      formValues.toDateTime = moment(formValues.toDateTime).format('YYYY-MM-DDTHH:mmZ')
      formValues.fromDateTime = moment(formValues.fromDateTime).format('YYYY-MM-DDTHH:mmZ')
      await onSubmit(formValues)
      pop()
    },
    [onSubmit, pop],
  )

  return (
    <AppForm form={form} onSubmit={onSubmitHandle} hasInitialValues={isUpdateForm}>
      <Controller
        name="fromDateTime"
        render={({ field }) => (
          <DateTimePickerSplitComponent
            label={translate('fromDateAndTime')}
            onChange={value => form.setValue('fromDateTime', value)}
            name="fromDateTime"
            fullWidth={true}
          />
        )}
      />

      <Controller
        name="toDateTime"
        render={({ field }) => (
          <DateTimePickerSplitComponent
            label={translate('toDateAndTime')}
            onChange={value => form.setValue('toDateTime', value)}
            name="toDateTime"
            fullWidth={true}
          />
        )}
      />

      <FormTextField rows={2} multiline name="description" label={translate('description', { ns: 'common' })} />
      <FormSelectField
        variant="outlined"
        getOptionLabel={row => row.name}
        label={translate('resource', { ns: 'task' })}
        data={resources?.items || []}
        name={'resourceId'}
        clearOnBlur={false}
        clearOnEscape={false}
      />
      <FormSelectField
        variant="outlined"
        getOptionLabel={row => row.typeName}
        label={translate('timeReportType')}
        data={timeReportTypes?.items || []}
        name={'timeReportTypeId'}
        clearOnBlur={false}
        clearOnEscape={false}
      />
      <Box display={'flex'} flexDirection={'row'} justifyContent={'end'}>
        <FormSubmitButton createText={translate('add', { ns: 'common' })} />
        <Button key="locality-form-cancel" onClick={pop}>
          {translate('cancel', { ns: 'common' })}
        </Button>
      </Box>
    </AppForm>
  )
}

export default TimeReportForm